// libs/features/global-search/feature-global-search/src/lib/components/filter-panel/filter-panel.component.ts

import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, viewChild} from '@angular/core'; // Added OnInit
import {FormsModule} from '@angular/forms';
import {FilterDefinition, GlobalSearchFilterType} from '@global-search/data-access';
import {TranslateModule} from '@ngx-translate/core';
import {IconComponent} from '@shared/icon';
import {CalendarModule} from 'primeng/calendar';
import {DropdownModule} from 'primeng/dropdown';
import {CategoryFilters} from '../../store/global-search-ui.state';
import {GlobalSearchUiStore} from '../../store/global-search-ui.store';
import {MultiSelect, MultiSelectModule} from 'primeng/multiselect';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
	selector: 'dc-filter-panel',
	imports: [
		FormsModule,
		IconComponent,
		TranslateModule,
		CalendarModule, // *** הוספה למערך הייבואים ***
		DropdownModule,
		MultiSelectModule,
	],
	templateUrl: './filter-panel.component.html',
	styleUrl: './filter-panel.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterPanelComponent {
	// --- Inputs ---
	readonly definitions = input.required<FilterDefinition<any, any>[]>();
	readonly currentFilters = input<CategoryFilters>({});
	// --- Outputs ---
	readonly filterChanged = output<{filter: FilterDefinition<any>; value: any}>();
	protected readonly selectEl = viewChild<MultiSelect>(MultiSelect);
	protected readonly uiStore = inject(GlobalSearchUiStore);
	// --- Exposing Enum to Template ---
	protected readonly GlobalSearchFilterType = GlobalSearchFilterType;
	// --- Component State ---
	protected today = new Date(); // For potential use in p-calendar maxDate
	protected readonly definitionsByFilterType$ = computed(() => {
		const definitions = this.definitions();
		return definitions.reduce(
			(acc, definition) => {
				const type = definition.type;
				if (type) acc[type] = definition;
				return acc;
			},
			{} as Record<GlobalSearchFilterType, FilterDefinition<any>>,
		);
	});
	private readonly destroyRef = inject(DestroyRef);

	// --- Constructor ---
	constructor() {
		this.uiStore.globalSearchContainerClicked$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
			// if the click is otside the selectEl and it's open => close it
			if (this.selectEl() && this.selectEl().overlayVisible && !this.selectEl().el.nativeElement.contains(event.target)) {
				this.selectEl().hide();
			}
		});
	}

	// --- Methods ---

	protected getCurrentBooleanValue(filterType: GlobalSearchFilterType): boolean {
		return this.currentFilters()[filterType] ?? false;
	}

	/**
	 * Handles value changes from filter controls (select, input[text], p-calendar).
	 * Emits the change via the filterChanged output.
	 * @param filterType The type of the filter that changed.
	 * @param value The new value from the control.
	 */
	protected onValueChange(definition: FilterDefinition<any>, value: any): void {
		this.filterChanged.emit({filter: definition, value: value || undefined});
	}

	protected onCheckboxChange(definition: FilterDefinition<any>, event: Event): void {
		const target = event.target as HTMLInputElement;
		const value = target.checked;
		this.filterChanged.emit({filter: definition, value: value ? true : undefined});
	}

	protected onSelectPanelHide(): void {
		this.uiStore.on('SelectPanelClosed');
	}

	protected trackByFilterType(index: number, definition: FilterDefinition<any, any>): GlobalSearchFilterType {
		return definition.type;
	}
}
